<!--
@component
GroupTable

Standings/Results tab UI for the Groups view.

Used in three contexts:
- `/groups/standings/` and `/groups/results/` (dedicated routes) → pass `routed`
- `HomePage` (`/`) → default (unrouted, in-place tab swap)
- `pages/(embeds)/embeds/en/groups/+page.svelte` (iframed embed) → default

See #39 for the rationale behind the hybrid approach.
-->

<script lang="ts">
  import { fly } from 'svelte/transition';
  import { appState } from '$lib/stores/index.svelte';
  import Standings from './Standings/index.svelte';
  import Results from './Results/index.svelte';
  import { Block } from '@reuters-graphics/graphics-components';
  import { page } from '$app/state';
  import { resolve } from '$app/paths';
  import type { Component } from 'svelte';

  type TabId = 'standings' | 'results';

  type Tab = {
    id: TabId;
    label: string;
    href: string;
    component: Component<{ data: unknown }>;
  };

  type Props = {
    /** Opta `matches` payload. */
    data: unknown;
    /**
     * When `true`, tabs render as `<a href>` and the active tab is driven by
     * the URL pathname (`/groups/standings/` vs `/groups/results/`). Used on
     * the dedicated Groups routes.
     *
     * When `false` (default), tabs render as `<button>` and the active tab
     * lives in component-local state — no navigation occurs. Used on
     * HomePage and the embed, where `GroupTable` is rendered inside a
     * different host page that must not change URL.
     */
    routed?: boolean;
  };

  let { data, routed = false }: Props = $props();

  const tabs: readonly Tab[] = [
    {
      id: 'standings',
      label: 'Standings',
      href: resolve('/groups/standings/'),
      component: Standings as Component<{ data: unknown }>,
    },
    {
      id: 'results',
      label: 'Results',
      href: resolve('/groups/results/'),
      component: Results as Component<{ data: unknown }>,
    },
  ] as const;

  const DEFAULT_TAB = tabs[0];

  /** Local tab state for the unrouted mode. Ignored when `routed`. */
  let localTabId: TabId = $state(DEFAULT_TAB.id);

  const activeTab = $derived(
    routed ?
      (tabs.find((t) => page.url.pathname.endsWith(`/${t.id}/`)) ?? DEFAULT_TAB)
    : (tabs.find((t) => t.id === localTabId) ?? DEFAULT_TAB)
  );

  const selectTab = (id: TabId) => {
    if (appState.isLoading) return;
    localTabId = id;
  };
</script>

<Block id="groups-table" class="fpy-3">
  <div
    class="tablist fpx-0 flex font-sans justify-center"
    role="tablist"
    aria-label="Group view"
  >
    {#each tabs as tab (tab.id)}
      {@const isActive = activeTab.id === tab.id}
      {#if routed}
        <a
          class="tab"
          role="tab"
          id="tab-{tab.id}"
          href={tab.href}
          aria-controls="panel-{tab.id}"
          aria-selected={isActive}
          aria-current={isActive ? 'page' : undefined}
          tabindex={isActive ? 0 : -1}
          class:active={isActive}
          class:disabled={appState.isLoading}
          aria-disabled={appState.isLoading ? 'true' : undefined}
        >
          {tab.label}
        </a>
      {:else}
        <button
          type="button"
          class="tab"
          role="tab"
          id="tab-{tab.id}"
          aria-controls="panel-{tab.id}"
          aria-selected={isActive}
          tabindex={isActive ? 0 : -1}
          class:active={isActive}
          class:disabled={appState.isLoading}
          disabled={appState.isLoading}
          onclick={() => selectTab(tab.id)}
        >
          {tab.label}
        </button>
      {/if}
    {/each}
  </div>
</Block>

<Block width="widest">
  <!--
    When `routed`, the URL is the source of truth for which tab to show
    (e.g. `/groups/results/` must render Results even while data is
    loading, otherwise the user sees the wrong placeholder type — see
    #23). Only fall back to `DEFAULT_TAB` during loading in the unrouted
    mode, where the in-place tab swap on HomePage/embed wants a
    consistent "first paint" tab regardless of stale local state.
  -->
  {@const PanelTab = appState.isLoading && !routed ? DEFAULT_TAB : activeTab}
  {@const ActiveComponent = PanelTab.component}
  {#key PanelTab.id}
    <div
      id="panel-{PanelTab.id}"
      role="tabpanel"
      aria-labelledby="tab-{PanelTab.id}"
      in:fly|global={{ y: -15, duration: 500 }}
    >
      <ActiveComponent {data} />
    </div>
  {/key}
</Block>

<style lang="scss">
  @use '../../styles/mixins' as mixins;

  .tablist {
    margin: auto;
  }

  .tab:first-child {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right: none;
  }
  .tab:last-child {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left: none;
  }

  .tab {
    @include mixins.button-primary;
    width: 150px;
    text-decoration: none;
    display: inline-block;
    text-align: center;

    &.disabled,
    &:disabled,
    &[aria-disabled='true'] {
      pointer-events: none;
      cursor: default;
    }

    &.active {
      background: var(--theme-colour-muted-grey);
      @include mixins.hard-shadow;
      color: var(--theme-colour-text-primary);
      @include mixins.font-medium;
    }
  }
</style>
